/**
 * Client-Side Audio Storage using IndexedDB
 * Stores audio on user's device instead of MongoDB server
 * Zero server storage cost!
 */

const DB_NAME = 'TaraanayaAudioCache';
const DB_VERSION = 1;
const STORE_NAME = 'audioFiles';

// Open IndexedDB connection
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('type', 'type', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
  });
};

/**
 * Save audio to local device storage
 * @param {string} userId - User ID
 * @param {string} type - 'past_story' | 'future_reel' | 'domain_tts'
 * @param {string} key - Unique key (e.g., 'health_hinglish', 'intro_english')
 * @param {string} audioBase64 - Base64 encoded audio
 * @param {object} metadata - Additional metadata
 */
export const saveAudioLocal = async (userId, type, key, audioBase64, metadata = {}) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const id = `${userId}_${type}_${key}`;
    
    await store.put({
      id,
      userId,
      type,
      key,
      audioBase64,
      metadata,
      createdAt: new Date().toISOString(),
      sizeBytes: audioBase64.length * 0.75 // Approximate decoded size
    });
    
    console.log(`✅ Saved audio locally: ${id} (${(audioBase64.length * 0.75 / 1024 / 1024).toFixed(2)} MB)`);
    return true;
  } catch (error) {
    console.error('❌ Failed to save audio locally:', error);
    return false;
  }
};

/**
 * Get audio from local device storage
 * @param {string} userId - User ID
 * @param {string} type - 'past_story' | 'future_reel' | 'domain_tts'
 * @param {string} key - Unique key
 * @returns {string|null} Base64 audio or null if not found
 */
export const getAudioLocal = async (userId, type, key) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const id = `${userId}_${type}_${key}`;
    const result = await new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (result) {
      console.log(`✅ Found cached audio: ${id}`);
      return result.audioBase64;
    }
    
    console.log(`⚠️ No cached audio: ${id}`);
    return null;
  } catch (error) {
    console.error('❌ Failed to get audio locally:', error);
    return null;
  }
};

/**
 * Check if audio exists locally and is valid (not corrupted/empty)
 */
export const hasAudioLocal = async (userId, type, key) => {
  const audio = await getAudioLocal(userId, type, key);
  // Audio must exist AND be substantial (>5KB base64 = ~3.75KB real audio)
  return audio !== null && audio.length > 5000;
};

/**
 * Get all cached audio for a user
 */
export const getUserAudioCache = async (userId) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('userId');
    
    const results = await new Promise((resolve, reject) => {
      const request = index.getAll(userId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    const totalSize = results.reduce((sum, r) => sum + (r.sizeBytes || 0), 0);
    
    return {
      count: results.length,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
      items: results.map(r => ({
        type: r.type,
        key: r.key,
        sizeMB: ((r.sizeBytes || 0) / 1024 / 1024).toFixed(2),
        createdAt: r.createdAt
      }))
    };
  } catch (error) {
    console.error('❌ Failed to get user cache:', error);
    return { count: 0, totalSizeMB: '0', items: [] };
  }
};

/**
 * Clear all audio cache for a user
 */
export const clearUserAudioCache = async (userId) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('userId');
    
    const keys = await new Promise((resolve, reject) => {
      const request = index.getAllKeys(userId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    for (const key of keys) {
      await store.delete(key);
    }
    
    console.log(`✅ Cleared ${keys.length} cached audio files for user`);
    return keys.length;
  } catch (error) {
    console.error('❌ Failed to clear cache:', error);
    return 0;
  }
};

/**
 * Clear all audio cache (all users)
 */
export const clearAllAudioCache = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await store.clear();
    console.log('✅ Cleared all audio cache');
    return true;
  } catch (error) {
    console.error('❌ Failed to clear all cache:', error);
    return false;
  }
};

/**
 * Get total storage used by audio cache
 */
export const getAudioCacheStats = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    const results = await new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    const totalSize = results.reduce((sum, r) => sum + (r.sizeBytes || 0), 0);
    const byType = {};
    
    results.forEach(r => {
      if (!byType[r.type]) byType[r.type] = { count: 0, sizeMB: 0 };
      byType[r.type].count++;
      byType[r.type].sizeMB += (r.sizeBytes || 0) / 1024 / 1024;
    });
    
    return {
      totalFiles: results.length,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
      byType
    };
  } catch (error) {
    console.error('❌ Failed to get cache stats:', error);
    return { totalFiles: 0, totalSizeMB: '0', byType: {} };
  }
};

// Default export
const LocalAudioCache = {
  saveAudioLocal,
  getAudioLocal,
  hasAudioLocal,
  getUserAudioCache,
  clearUserAudioCache,
  clearAllAudioCache,
  getAudioCacheStats
};

export default LocalAudioCache;
